import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { HelpTooltip } from "../../../shared/ui/help/HelpTooltip";

import { Box, Stack, Typography, IconButton } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  OnboardingStepLayout,
  TextInput,
  CountrySelect,
  ToggleField,
} from "./../../../shared/ui/index";
import { Step2Schema, type Step2Input } from "../../../shared/schemas/step2";
import { useTranslation, Trans } from "react-i18next";
import { useEffect, useMemo } from "react";

type AllowedCountryCode =
  | "PT"
  | "AT"
  | "BE"
  | "BG"
  | "HR"
  | "CY"
  | "CZ"
  | "DK"
  | "EE"
  | "FI"
  | "FR"
  | "DE"
  | "GR"
  | "HU"
  | "IE"
  | "IT"
  | "LV"
  | "LT"
  | "LU"
  | "MT"
  | "NL"
  | "PL"
  | "RO"
  | "SK"
  | "SI"
  | "ES"
  | "SE"
  | "GB";

function isAllowedCountryCode(value: unknown): value is AllowedCountryCode {
  return (
    typeof value === "string" &&
    [
      "PT",
      "AT",
      "BE",
      "BG",
      "HR",
      "CY",
      "CZ",
      "DK",
      "EE",
      "FI",
      "FR",
      "DE",
      "GR",
      "HU",
      "IE",
      "IT",
      "LV",
      "LT",
      "LU",
      "MT",
      "NL",
      "PL",
      "RO",
      "SK",
      "SI",
      "ES",
      "SE",
      "GB",
    ].includes(value)
  );
}

export type Step2FormValues = Step2Input;

type Step2Props = {
  currentStep: number;
  totalSteps: number;
  initialValues?: Step2FormValues;
  onCompleted: (data: Step2FormValues) => void;
  onBack: () => void;
};

export default function Step2_CompanyInfo({
  currentStep,
  totalSteps,
  initialValues,
  onCompleted,
  onBack,
}: Step2Props) {
  const methods = useForm<Step2FormValues>({
    resolver: zodResolver(Step2Schema),
    defaultValues: initialValues ?? {
      vatNumber: "",
      billingStreet: "",
      billingStreetNumber: "",
      billingStreetNumberSuffix: "",
      billingPostalCode: "",
      billingCity: "",
      billingDistrict: "",
      billingCountry: "PT",
      differentCompanyAddress: false,
      companyStreet: "",
      companyStreetNumber: "",
      companyStreetNumberSuffix: "",
      companyPostalCode: "",
      companyCity: "",
      companyDistrict: "",
      companyCountry: "PT",
    },
  });

  const { handleSubmit, watch, setValue } = methods;
  const { t } = useTranslation();

  const differentCompanyAddress = watch("differentCompanyAddress");
  const billingCountry = watch("billingCountry");

  const onSubmit = handleSubmit((data) => {
    sessionStorage.setItem("step2", JSON.stringify(data));
    onCompleted(data);
  });

  const handleToggleDifferentAddress = (checked: boolean) => {
    if (!checked) {
      setValue("companyStreet", "");
      setValue("companyStreetNumber", "");
      setValue("companyPostalCode", "");
      setValue("companyCity", "");
      setValue("companyDistrict", "");
      setValue("companyCountry", "PT");
    }
  };

  const step1CountryFromSession: AllowedCountryCode = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("step1");
      if (!raw) return "PT";
      const parsed: unknown = JSON.parse(raw);

      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "country" in parsed
      ) {
        const c = (parsed as { country?: unknown }).country;
        if (isAllowedCountryCode(c)) return c;
      }
      return "PT";
    } catch {
      return "PT";
    }
  }, []);

  useEffect(() => {
    if (billingCountry !== step1CountryFromSession) {
      setValue("billingCountry", step1CountryFromSession, {
        shouldValidate: true,
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [billingCountry, setValue, step1CountryFromSession]);

  return (
    <FormProvider {...methods}>
      <OnboardingStepLayout
        step={currentStep}
        totalSteps={totalSteps}
        title={t("onboarding.step2.title")}
        subtitle=""
        onNext={onSubmit}
        nextLabel={t("onboarding.step2.actions.next")}
        onBack={onBack}
        backLabel={t("onboarding.step2.actions.back")}
      >
        <Stack spacing={3}>
          <TextInput<Step2FormValues, "vatNumber">
            name="vatNumber"
            label={t("onboarding.step2.vatNumber.label")}
            placeholder={t("onboarding.step2.vatNumber.placeholder")}
            required
            inputProps={{
              inputMode: "numeric",
              pattern: "[0-9]*",
              maxLength: 9,
              onInput: (e: React.FormEvent<HTMLInputElement>) => {
                const el = e.currentTarget;
                el.value = el.value.replace(/\D/g, "").slice(0, 9);
              },
            }}
          />

          <Box>
            <Box sx={{ mt: "20px" }}>
              <SectionHeader
                title={t("onboarding.step2.billing.title")}
                tooltip={t("onboarding.step2.billing.tooltip")}
              />
            </Box>

            <Stack spacing={2.5} mt={1.5}>
              <TextInput<Step2FormValues, "billingStreet">
                name="billingStreet"
                label={t("onboarding.step2.billing.street.label")}
                placeholder={t("onboarding.step2.billing.street.placeholder")}
                required
              />

              <TextInput<Step2FormValues, "billingStreetNumber">
                name="billingStreetNumber"
                label={t("onboarding.step2.billing.streetNumber.label")}
                placeholder={t(
                  "onboarding.step2.billing.streetNumber.placeholder"
                )}
                required
                type="text"
                inputProps={{
                  inputMode: "numeric",
                  onInput: (e: React.FormEvent<HTMLInputElement>) => {
                    const el = e.currentTarget;
                    el.value = el.value.replace(/\D/g, "");
                  },
                }}
              />

              <TextInput<Step2FormValues, "billingStreetNumberSuffix">
                name="billingStreetNumberSuffix"
                label={t("onboarding.step2.billing.streetNumberSuffix.label")}
                placeholder={t(
                  "onboarding.step2.billing.streetNumberSuffix.placeholder"
                )}
                // Optional field allowing alphanumeric input
                inputProps={{
                  maxLength: 10,
                }}
              />

              <TextInput<Step2FormValues, "billingPostalCode">
                name="billingPostalCode"
                label={t("onboarding.step2.billing.postalCode.label")}
                placeholder={t(
                  "onboarding.step2.billing.postalCode.placeholder"
                )}
                required
              />

              <TextInput<Step2FormValues, "billingCity">
                name="billingCity"
                label={t("onboarding.step2.billing.city.label")}
                placeholder={t("onboarding.step2.billing.city.placeholder")}
                required
              />

              <TextInput<Step2FormValues, "billingDistrict">
                name="billingDistrict"
                label={t("onboarding.step2.billing.district.label")}
                placeholder={t("onboarding.step2.billing.district.placeholder")}
              />

              <CountrySelect<Step2FormValues, "billingCountry">
                name="billingCountry"
                label={t("onboarding.step2.billing.country.label")}
                required
                disabled
                allowedCountryCodes={[step1CountryFromSession]}
              />
            </Stack>
          </Box>

          <ToggleField<Step2FormValues, "differentCompanyAddress">
            name="differentCompanyAddress"
            onToggle={handleToggleDifferentAddress}
            label={
              <Trans
                i18nKey="onboarding.step2.differentCompanyAddress.label"
                components={[
                  <Box
                    key="billing"
                    component="span"
                    sx={{ fontStyle: "italic" }}
                  />,
                  <Box
                    key="company"
                    component="span"
                    sx={{ fontStyle: "italic" }}
                  />,
                ]}
              />
            }
          />

          {differentCompanyAddress && (
            <Box mt={2}>
              <SectionHeader
                title={t("onboarding.step2.companyAddress.title")}
                tooltip={t("onboarding.step2.companyAddress.tooltip")}
              />

              <Stack spacing={2.5} mt={1.5}>
                <TextInput<Step2FormValues, "companyStreet">
                  name="companyStreet"
                  label={t("onboarding.step2.companyAddress.street.label")}
                  placeholder={t(
                    "onboarding.step2.companyAddress.street.placeholder"
                  )}
                  required
                />

                <TextInput<Step2FormValues, "companyStreetNumber">
                  name="companyStreetNumber"
                  label={t(
                    "onboarding.step2.companyAddress.streetNumber.label"
                  )}
                  placeholder={t(
                    "onboarding.step2.companyAddress.streetNumber.placeholder"
                  )}
                  required
                  type="text"
                  inputProps={{
                    inputMode: "numeric",
                    onInput: (e: React.FormEvent<HTMLInputElement>) => {
                      const el = e.currentTarget;
                      el.value = el.value.replace(/\D/g, "");
                    },
                  }}
                />

                <TextInput<Step2FormValues, "companyStreetNumberSuffix">
                  name="companyStreetNumberSuffix"
                  label={t("onboarding.step2.companyAddress.streetNumberSuffix.label")}
                  placeholder={t(
                    "onboarding.step2.companyAddress.streetNumberSuffix.placeholder"
                  )}
                  // Optional field allowing alphanumeric input
                  inputProps={{
                    maxLength: 10,
                  }}
                />

                <TextInput<Step2FormValues, "companyPostalCode">
                  name="companyPostalCode"
                  label={t("onboarding.step2.companyAddress.postalCode.label")}
                  placeholder={t(
                    "onboarding.step2.companyAddress.postalCode.placeholder"
                  )}
                  required
                />

                <TextInput<Step2FormValues, "companyCity">
                  name="companyCity"
                  label={t("onboarding.step2.companyAddress.city.label")}
                  placeholder={t(
                    "onboarding.step2.companyAddress.city.placeholder"
                  )}
                  required
                />

                <TextInput<Step2FormValues, "companyDistrict">
                  name="companyDistrict"
                  label={t("onboarding.step2.companyAddress.district.label")}
                  placeholder={t(
                    "onboarding.step2.companyAddress.district.placeholder"
                  )}
                />

                <CountrySelect<Step2FormValues, "companyCountry">
                  name="companyCountry"
                  label={t("onboarding.step2.companyAddress.country.label")}
                  required
                />
              </Stack>
            </Box>
          )}
        </Stack>
      </OnboardingStepLayout>
    </FormProvider>
  );
}

function SectionHeader({
  title,
  tooltip,
}: {
  title: string;
  tooltip?: string;
}) {
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography
        sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#1A1A1A" }}
      >
        {title}
      </Typography>

      {tooltip && (
        <HelpTooltip title={tooltip} arrow>
          <IconButton
            size="small"
            sx={{
              p: 0,
              ml: 0.25,
              color: "#6A7A89",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </HelpTooltip>
      )}
    </Box>
  );
}
